import api from '@/lib/api';
import type {
  Package,
  PackageElement,
  PackageWithCount,
  PackageElementType,
  PackageElementPer,
} from '@pms/shared';

/** Detail response = package header + its element rows. */
export interface PackageDetail extends Package {
  elements: PackageElement[];
}

export interface ListPackagesQuery {
  propertyId: string;
  search?: string | undefined;
  isActive?: boolean | undefined;
}

/** Element payload for create/update (no id — elements are replaced wholesale). */
export interface PackageElementPayload {
  name: string;
  elementType: PackageElementType;
  amount?: number | undefined;
  per: PackageElementPer;
}

export interface CreatePackagePayload {
  propertyId: string;
  name: string;
  code: string;
  description?: string | undefined;
  isActive?: boolean | undefined;
  elements: PackageElementPayload[];
}

export interface UpdatePackagePayload {
  name?: string | undefined;
  code?: string | undefined;
  description?: string | null | undefined;
  isActive?: boolean | undefined;
  elements?: PackageElementPayload[] | undefined;
}

export async function fetchPackages(
  query: ListPackagesQuery,
): Promise<PackageWithCount[]> {
  const params: Record<string, string> = { propertyId: query.propertyId };
  if (query.search?.trim()) params['search'] = query.search.trim();
  if (query.isActive !== undefined) params['isActive'] = String(query.isActive);
  const { data } = await api.get<PackageWithCount[]>('/packages', { params });
  return data;
}

export async function fetchPackage(id: string): Promise<PackageDetail> {
  const { data } = await api.get<PackageDetail>(`/packages/${id}`);
  return data;
}

export async function createPackage(
  payload: CreatePackagePayload,
): Promise<PackageDetail> {
  const { data } = await api.post<PackageDetail>('/packages', payload);
  return data;
}

export async function updatePackage(
  id: string,
  payload: UpdatePackagePayload,
): Promise<PackageDetail> {
  const { data } = await api.patch<PackageDetail>(`/packages/${id}`, payload);
  return data;
}

export async function deletePackage(id: string): Promise<void> {
  await api.delete(`/packages/${id}`);
}
